import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga';
import Modal from '../../components/modal/Modal';
import { Button, ActionButtonSpinner, getActionButtonLoadingClassName } from '@ui';
import { resizeImageToJpeg, submitRefundRequest } from '../../hookServices/feedbackApi';
import { isNativeShellApp } from '../../hookServices/clientId';
import styles from './ServiceRequestModal.module.css';

const TERMS_REFUND_PATH = '/contract#refund';

const DESC_MIN = 10;
const DESC_MAX = 3000;
const NAME_MIN = 2;
const PHONE_RE = /^\+?[0-9 ()-]{9,20}$/;
const PROBLEM_TYPES = ['port_not_working', 'station_offline', 'cable_damage', 'payment', 'other'];
const PHOTO_SLOTS = [
  { kind: 'receipt', field: 'photoReceipt' },
  { kind: 'car', field: 'photoCar' },
];

const emptyPhotos = () => ({
  receipt: { file: null, preview: null },
  car: { file: null, preview: null },
});

function cardDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function validateFields(fields) {
  const errors = {};
  if (!String(fields.port || '').trim()) errors.port = 'portRequired';
  if (!String(fields.fullName || '').trim() || String(fields.fullName).trim().length < NAME_MIN) {
    errors.fullName = 'nameRequired';
  }
  if (!String(fields.phone || '').trim()) {
    errors.phone = 'phoneRequired';
  } else if (!PHONE_RE.test(String(fields.phone).trim())) {
    errors.phone = 'phoneInvalid';
  }
  if (!Array.isArray(fields.problemTypes) || fields.problemTypes.length === 0) {
    errors.reason = 'reasonRequired';
  } else if (fields.problemTypes.some(key => !PROBLEM_TYPES.includes(key))) {
    errors.reason = 'reasonRequired';
  }
  const desc = String(fields.description || '').trim();
  if (desc.length < DESC_MIN) errors.description = 'descriptionRequired';
  const digits = cardDigits(fields.cardNumber);
  if (digits.length < 13 || digits.length > 19) errors.cardNumber = 'cardRequired';
  const amount = Number(String(fields.amountUah || '').replace(',', '.'));
  if (!Number.isFinite(amount) || amount <= 0) errors.amountUah = 'amountRequired';
  if (fields.contactedSupport !== true && fields.contactedSupport !== false) {
    errors.contactedSupport = 'contactedRequired';
  }
  if (![1, 2, 3, 4, 5].includes(Number(fields.chargingRating))) {
    errors.chargingRating = 'ratingRequired';
  }
  if (!fields.photos?.receipt?.file) errors.photoReceipt = 'receiptPhotoRequired';
  if (!fields.photos?.car?.file) errors.photoCar = 'carPhotoRequired';
  return errors;
}

export default function RefundRequestModal({
  modalOpen,
  setModalOpen,
  stationNumber,
  clientId,
  invoiceId,
  jobId,
  onRecoverBalance,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canRecoverBalance = isNativeShellApp();
  const fileRefs = useRef({});
  const [port, setPort] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [problemTypes, setProblemTypes] = useState([]);
  const [description, setDescription] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [amountUah, setAmountUah] = useState('');
  const [contactedSupport, setContactedSupport] = useState(null);
  const [chargingRating, setChargingRating] = useState(0);
  const [photos, setPhotos] = useState(emptyPhotos);
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [problemsOpen, setProblemsOpen] = useState(false);
  const problemsRef = useRef(null);

  const hasSession = Boolean(String(invoiceId || '').trim());
  const problemSummary =
    problemTypes.length === 0
      ? t('refundForm.problemTypesPlaceholder')
      : problemTypes.map(key => t(`refundForm.problemTypes.${key}`)).join(', ');

  function handleRecoverBalance() {
    if (!canRecoverBalance) return;
    ReactGA.event({
      category: 'refund_request',
      action: 'recover_balance_alternative',
      label: String(port || stationNumber || ''),
    });
    setModalOpen(false);
    if (typeof onRecoverBalance === 'function') {
      onRecoverBalance();
      return;
    }
    navigate('/history');
  }

  useEffect(() => {
    if (!modalOpen) return undefined;
    setPort(String(stationNumber || ''));
    setFullName('');
    setPhone('');
    setProblemTypes([]);
    setProblemsOpen(false);
    setDescription('');
    setCardNumber('');
    setAmountUah('');
    setContactedSupport(null);
    setChargingRating(0);
    setPhotos(prev => {
      Object.values(prev).forEach(p => {
        if (p.preview) URL.revokeObjectURL(p.preview);
      });
      return emptyPhotos();
    });
    Object.values(fileRefs.current).forEach(el => {
      if (el) el.value = '';
    });
    setErrorKey(null);
    setFieldErrors({});
    setSuccess(false);
    setSubmitting(false);
    return undefined;
  }, [modalOpen, invoiceId, jobId, stationNumber]);

  useEffect(() => {
    if (!problemsOpen) return undefined;
    function onPointerDown(event) {
      if (problemsRef.current && !problemsRef.current.contains(event.target)) {
        setProblemsOpen(false);
      }
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') setProblemsOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [problemsOpen]);

  function toggleProblemType(key) {
    setProblemTypes(prev => {
      const next = prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key];
      return PROBLEM_TYPES.filter(item => next.includes(item));
    });
    clearFieldError('reason');
  }

  useEffect(() => {
    return () => {
      Object.values(photos).forEach(p => {
        if (p.preview) URL.revokeObjectURL(p.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clearFieldError(key) {
    setFieldErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setErrorKey(null);
  }

  function handlePhotoChange(kind, event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotos(prev => {
      const next = { ...prev };
      if (next[kind]?.preview) URL.revokeObjectURL(next[kind].preview);
      next[kind] = { file, preview: URL.createObjectURL(file) };
      return next;
    });
    clearFieldError(kind === 'receipt' ? 'photoReceipt' : 'photoCar');
  }

  function clearPhoto(kind) {
    setPhotos(prev => {
      const next = { ...prev };
      if (next[kind]?.preview) URL.revokeObjectURL(next[kind].preview);
      next[kind] = { file: null, preview: null };
      return next;
    });
    if (fileRefs.current[kind]) fileRefs.current[kind].value = '';
  }

  async function handleSubmit() {
    if (submitting) return;
    const errors = validateFields({
      port,
      fullName,
      phone,
      problemTypes,
      description,
      cardNumber,
      amountUah,
      contactedSupport,
      chargingRating,
      photos,
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setErrorKey('incomplete');
      return;
    }

    setSubmitting(true);
    setErrorKey(null);
    try {
      const photoReceipt = await resizeImageToJpeg(photos.receipt.file);
      const photoCar = await resizeImageToJpeg(photos.car.file);
      await submitRefundRequest({
        stationNumber: String(port).trim(),
        invoiceId: hasSession ? String(invoiceId).trim() : null,
        jobId: hasSession && jobId != null ? String(jobId) : null,
        clientId,
        phone: phone.trim(),
        fullName: fullName.trim(),
        problemTypes,
        description: description.trim(),
        cardNumber: cardDigits(cardNumber),
        amountUah: String(amountUah).trim().replace(',', '.'),
        contactedSupport,
        chargingRating: Number(chargingRating),
        photoReceipt,
        photoCar,
      });
      ReactGA.event({
        category: 'refund_request',
        action: 'submit',
        label: String(port || ''),
      });
      setSuccess(true);
    } catch (err) {
      const detail = err?.detail || err?.message || '';
      if (err?.status === 429 || detail === 'RATE_LIMITED') {
        setErrorKey('rateLimited');
      } else if (detail === 'INVALID_PHONE') {
        setFieldErrors({ phone: 'phoneInvalid' });
        setErrorKey('invalidPhone');
      } else if (detail === 'INVALID_CARD') {
        setFieldErrors({ cardNumber: 'cardRequired' });
        setErrorKey('incomplete');
      } else if (err?.status === 409 || detail === 'DUPLICATE_INVOICE') {
        setErrorKey('duplicate');
      } else if (err?.status === 413 || detail === 'BODY_TOO_LARGE') {
        setErrorKey('tooLarge');
      } else {
        setErrorKey('generic');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const missingLabels = Object.keys(fieldErrors).map(key => {
    const map = {
      port: t('refundForm.portLabel'),
      fullName: t('refundForm.fullNameLabel'),
      phone: t('refundForm.phoneLabel'),
      reason: t('refundForm.problemTypesLabel'),
      description: t('refundForm.descriptionLabel'),
      cardNumber: t('refundForm.cardLabel'),
      amountUah: t('refundForm.amountLabel'),
      contactedSupport: t('refundForm.contactedLabel'),
      chargingRating: t('refundForm.ratingLabel'),
      photoReceipt: t('refundForm.photoSlots.receipt'),
      photoCar: t('refundForm.photoSlots.car'),
    };
    return map[key] || key;
  });

  return (
    <Modal modalOpen={modalOpen} setModalOpen={setModalOpen} scrollable>
      <div className={styles.root}>
        <button type="button" className={styles.close} aria-label={t('close')} onClick={() => setModalOpen(false)}>
          ×
        </button>

        {success ? (
          <>
            <h2 className={styles.title}>{t('refundForm.successTitle')}</h2>
            <p className={styles.successBody}>{t('refundForm.successBody')}</p>
            <Button onClick={() => setModalOpen(false)} className={styles.submit} style={{ marginTop: '16px' }}>
              <span>OK</span>
            </Button>
          </>
        ) : (
          <>
            <h2 className={styles.title}>{t('refundForm.title')}</h2>
            {!hasSession && <p className={styles.hint}>{t('refundForm.manualHint')}</p>}

            <div className={styles.termsBox}>
              <p className={styles.termsText}>
                <Trans
                  i18nKey="refundForm.termsSummary"
                  components={{
                    termsLink: (
                      <Link
                        to={TERMS_REFUND_PATH}
                        className={styles.termsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ),
                  }}
                />
              </p>
              <p className={styles.termsClause}>
                <strong>7.3.</strong> {t('refundForm.terms73')}
              </p>
              <p className={styles.termsClause}>
                <strong>7.4.</strong> {t('refundForm.terms74')}
              </p>
            </div>

            {canRecoverBalance && (
              <div className={styles.altBox}>
                <p className={styles.altText}>{t('refundForm.recoverAlternative')}</p>
                <Button type="button" className={styles.secondaryBtn} onClick={handleRecoverBalance}>
                  <span>{t('recoverBalance')}</span>
                </Button>
              </div>
            )}

            <label className={styles.label} htmlFor="rf-port">
              {t('refundForm.portLabel')}
            </label>
            <input
              id="rf-port"
              className={`${styles.input} ${fieldErrors.port ? styles.inputInvalid : ''}`}
              type="text"
              inputMode="numeric"
              value={port}
              onChange={e => {
                setPort(e.target.value.slice(0, 64));
                clearFieldError('port');
              }}
              placeholder={t('refundForm.portPlaceholder')}
              readOnly={hasSession}
              aria-invalid={Boolean(fieldErrors.port)}
            />
            {fieldErrors.port && <p className={styles.fieldError}>{t(`refundForm.errors.${fieldErrors.port}`)}</p>}

            <label className={styles.label} htmlFor="rf-name">
              {t('refundForm.fullNameLabel')}
            </label>
            <input
              id="rf-name"
              className={`${styles.input} ${fieldErrors.fullName ? styles.inputInvalid : ''}`}
              type="text"
              autoComplete="name"
              value={fullName}
              onChange={e => {
                setFullName(e.target.value.slice(0, 200));
                clearFieldError('fullName');
              }}
              placeholder={t('refundForm.fullNamePlaceholder')}
              aria-invalid={Boolean(fieldErrors.fullName)}
            />
            {fieldErrors.fullName && (
              <p className={styles.fieldError}>{t(`refundForm.errors.${fieldErrors.fullName}`)}</p>
            )}

            <label className={styles.label} htmlFor="rf-phone">
              {t('refundForm.phoneLabel')}
            </label>
            <input
              id="rf-phone"
              className={`${styles.input} ${fieldErrors.phone ? styles.inputInvalid : ''}`}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={e => {
                setPhone(e.target.value.slice(0, 32));
                clearFieldError('phone');
              }}
              placeholder={t('refundForm.phonePlaceholder')}
              aria-invalid={Boolean(fieldErrors.phone)}
            />
            {fieldErrors.phone && <p className={styles.fieldError}>{t(`refundForm.errors.${fieldErrors.phone}`)}</p>}

            <div className={styles.label} id="rf-problems-label">
              {t('refundForm.problemTypesLabel')}
            </div>
            <div className={styles.dropdown} ref={problemsRef}>
              <button
                id="rf-problems"
                type="button"
                className={`${styles.dropdownTrigger} ${fieldErrors.reason ? styles.inputInvalid : ''} ${
                  problemTypes.length === 0 ? styles.dropdownPlaceholder : ''
                }`}
                aria-haspopup="listbox"
                aria-expanded={problemsOpen}
                aria-labelledby="rf-problems-label"
                aria-invalid={Boolean(fieldErrors.reason)}
                aria-required="true"
                onClick={() => setProblemsOpen(open => !open)}
              >
                <span className={styles.dropdownTriggerText}>{problemSummary}</span>
                <span className={styles.dropdownChevron} aria-hidden="true">
                  {problemsOpen ? '▴' : '▾'}
                </span>
              </button>
              {problemsOpen && (
                <div className={styles.dropdownMenu} role="listbox" aria-multiselectable="true">
                  {PROBLEM_TYPES.map(key => {
                    const checked = problemTypes.includes(key);
                    return (
                      <label
                        key={key}
                        className={`${styles.dropdownOption} ${checked ? styles.dropdownOptionActive : ''}`}
                      >
                        <input
                          type="checkbox"
                          className={styles.dropdownCheckbox}
                          checked={checked}
                          onChange={() => toggleProblemType(key)}
                        />
                        <span>{t(`refundForm.problemTypes.${key}`)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {fieldErrors.reason && <p className={styles.fieldError}>{t(`refundForm.errors.${fieldErrors.reason}`)}</p>}

            <label className={styles.label} htmlFor="rf-description">
              {t('refundForm.descriptionLabel')}
            </label>
            <textarea
              id="rf-description"
              className={`${styles.textarea} ${fieldErrors.description ? styles.inputInvalid : ''}`}
              value={description}
              onChange={e => {
                setDescription(e.target.value.slice(0, DESC_MAX));
                clearFieldError('description');
              }}
              rows={4}
              placeholder={t('refundForm.descriptionPlaceholder')}
              aria-invalid={Boolean(fieldErrors.description)}
            />
            <p className={styles.hint}>{t('refundForm.descriptionHint')}</p>
            <div className={styles.counter}>
              {description.trim().length}/{DESC_MAX}
            </div>
            {fieldErrors.description && (
              <p className={styles.fieldError}>{t(`refundForm.errors.${fieldErrors.description}`)}</p>
            )}

            <label className={styles.label} htmlFor="rf-card">
              {t('refundForm.cardLabel')}
            </label>
            <input
              id="rf-card"
              className={`${styles.input} ${fieldErrors.cardNumber ? styles.inputInvalid : ''}`}
              type="text"
              inputMode="numeric"
              autoComplete="cc-number"
              value={cardNumber}
              onChange={e => {
                setCardNumber(e.target.value.slice(0, 23));
                clearFieldError('cardNumber');
              }}
              placeholder={t('refundForm.cardPlaceholder')}
              aria-invalid={Boolean(fieldErrors.cardNumber)}
            />
            {fieldErrors.cardNumber && (
              <p className={styles.fieldError}>{t(`refundForm.errors.${fieldErrors.cardNumber}`)}</p>
            )}

            <label className={styles.label} htmlFor="rf-amount">
              {t('refundForm.amountLabel')}
            </label>
            <input
              id="rf-amount"
              className={`${styles.input} ${fieldErrors.amountUah ? styles.inputInvalid : ''}`}
              type="text"
              inputMode="decimal"
              value={amountUah}
              onChange={e => {
                setAmountUah(e.target.value.slice(0, 12));
                clearFieldError('amountUah');
              }}
              placeholder={t('refundForm.amountPlaceholder')}
              aria-invalid={Boolean(fieldErrors.amountUah)}
            />
            {fieldErrors.amountUah && (
              <p className={styles.fieldError}>{t(`refundForm.errors.${fieldErrors.amountUah}`)}</p>
            )}

            <div className={styles.label}>{t('refundForm.contactedLabel')}</div>
            <div className={`${styles.chips} ${fieldErrors.contactedSupport ? styles.chipsInvalid : ''}`} role="group">
              {[true, false].map(val => (
                <button
                  key={String(val)}
                  type="button"
                  className={`${styles.chip} ${contactedSupport === val ? styles.chipActive : ''}`}
                  aria-pressed={contactedSupport === val}
                  onClick={() => {
                    setContactedSupport(val);
                    clearFieldError('contactedSupport');
                  }}
                >
                  {t(val ? 'refundForm.yes' : 'refundForm.no')}
                </button>
              ))}
            </div>
            {fieldErrors.contactedSupport && (
              <p className={styles.fieldError}>{t(`refundForm.errors.${fieldErrors.contactedSupport}`)}</p>
            )}

            <div className={styles.label}>{t('refundForm.ratingLabel')}</div>
            <div className={`${styles.chips} ${fieldErrors.chargingRating ? styles.chipsInvalid : ''}`} role="group">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  className={`${styles.chip} ${chargingRating === n ? styles.chipActive : ''}`}
                  aria-pressed={chargingRating === n}
                  onClick={() => {
                    setChargingRating(n);
                    clearFieldError('chargingRating');
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
            {fieldErrors.chargingRating && (
              <p className={styles.fieldError}>{t(`refundForm.errors.${fieldErrors.chargingRating}`)}</p>
            )}

            <div className={styles.label}>{t('refundForm.photosLabel')}</div>
            {PHOTO_SLOTS.map(({ kind }) => {
              const errKey = kind === 'receipt' ? 'photoReceipt' : 'photoCar';
              return (
                <div key={kind} className={styles.photoSlot}>
                  <input
                    id={`rf-photo-${kind}`}
                    ref={el => {
                      fileRefs.current[kind] = el;
                    }}
                    className={styles.fileInput}
                    type="file"
                    accept="image/*"
                    onChange={e => handlePhotoChange(kind, e)}
                  />
                  <label
                    className={`${styles.fileBtn} ${fieldErrors[errKey] ? styles.inputInvalid : ''}`}
                    htmlFor={`rf-photo-${kind}`}
                  >
                    {photos[kind].file ? photos[kind].file.name : t(`refundForm.photoSlots.${kind}`)}
                  </label>
                  {photos[kind].preview && (
                    <div className={styles.preview}>
                      <img className={styles.previewImg} src={photos[kind].preview} alt="" />
                      <Button type="button" variant="outline-alt" onClick={() => clearPhoto(kind)}>
                        {t('refundForm.removePhoto')}
                      </Button>
                    </div>
                  )}
                  {fieldErrors[errKey] && (
                    <p className={styles.fieldError}>{t(`refundForm.errors.${fieldErrors[errKey]}`)}</p>
                  )}
                </div>
              );
            })}

            {errorKey === 'incomplete' && missingLabels.length > 0 && (
              <p className={styles.error}>
                {t('refundForm.errors.incomplete')}: {missingLabels.join(', ')}
              </p>
            )}
            {errorKey && errorKey !== 'incomplete' && (
              <p className={styles.error}>{t(`refundForm.errors.${errorKey}`)}</p>
            )}

            <Button
              type="button"
              className={`${getActionButtonLoadingClassName(submitting)} ${styles.submit}`}
              disabled={submitting}
              aria-busy={submitting}
              onClick={handleSubmit}
            >
              {submitting ? <ActionButtonSpinner /> : <span>{t('refundForm.submit')}</span>}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
